import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Briefcase, AlertTriangle, Plus, Edit2, Trash2, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const AREAS = ['Personnel', 'Tools & Equipment', 'Training', 'IT Systems', 'Facilities', 'Finance', 'Other'];
const IMPACTS = [
  { v: 'low', l: 'Low', cls: 'bg-slate-700/40 border-slate-700 text-slate-300' },
  { v: 'medium', l: 'Medium', cls: 'bg-amber-500/15 border-amber-500/40 text-amber-300' },
  { v: 'high', l: 'High', cls: 'bg-orange-500/15 border-orange-500/40 text-orange-300' },
  { v: 'critical', l: 'Critical', cls: 'bg-rose-500/15 border-rose-500/40 text-rose-300' },
];
const STATUSES = [
  { v: 'open', l: 'Open', cls: 'bg-rose-500/15 border-rose-500/40 text-rose-300' },
  { v: 'in_progress', l: 'In progress', cls: 'bg-sky-500/15 border-sky-500/40 text-sky-300' },
  { v: 'mitigated', l: 'Mitigated', cls: 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300' },
  { v: 'closed', l: 'Closed', cls: 'bg-slate-700/40 border-slate-700 text-slate-300' },
];

const blank = { area: 'Personnel', shortfall: '', impact: 'medium', mitigation: '', owner: '', target_date: '', status: 'open' };

export default function ResourceAdequacyPage() {
  const { t } = useTranslation();
  const [data, setData] = useState({ items: [], summary: { total: 0, by_status: {}, by_impact: {} } });
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const token = localStorage.getItem('token');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/gaca-p1/resource-adequacy`, { headers: { Authorization: `Bearer ${token}` } });
      setData(await r.json());
    } catch { toast.error('Failed to load register'); }
    finally { setLoading(false); }
  }, [token]);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const submit = async () => {
    if (!form.shortfall.trim()) { toast.error('Shortfall description is required'); return; }
    try {
      const url = editing ? `${API}/api/gaca-p1/resource-adequacy/${editing}` : `${API}/api/gaca-p1/resource-adequacy`;
      const method = editing ? 'PATCH' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      toast.success(editing ? 'Entry updated' : 'Entry added');
      setOpen(false); setEditing(null); setForm(blank);
      fetchAll();
    } catch { toast.error('Save failed'); }
  };

  const startEdit = (it) => {
    setEditing(it.id);
    setForm({
      area: it.area || 'Personnel',
      shortfall: it.shortfall || '',
      impact: it.impact || 'medium',
      mitigation: it.mitigation || '',
      owner: it.owner || '',
      target_date: it.target_date || '',
      status: it.status || 'open',
    });
    setOpen(true);
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      await fetch(`${API}/api/gaca-p1/resource-adequacy/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      fetchAll();
    } catch { toast.error('Delete failed'); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-950"><Loader2 className="w-8 h-8 text-sky-400 animate-spin" /></div>;

  const impactCls = (v) => IMPACTS.find(i => i.v === v)?.cls || '';
  const statusCls = (v) => STATUSES.find(s => s.v === v)?.cls || '';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-6">
      <div className="max-w-[1400px] mx-auto space-y-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-lg bg-orange-500/15 border border-orange-500/30">
                <Briefcase className="w-6 h-6 text-orange-300" />
              </div>
              <h1 className="text-3xl font-bold text-white tracking-tight" data-testid="ra-title">
                {t('resourceAdequacy.title', 'Resource Adequacy Register')}
              </h1>
            </div>
            <p className="text-slate-400 max-w-3xl">
              {t('resourceAdequacy.subtitle', 'AE & Safety Manager log identified resource shortfalls and mitigations. Maps to GACA item 1.1.2 — anticipating and addressing SMS resource gaps.')}
            </p>
          </div>
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(blank); } }}>
            <DialogTrigger asChild>
              <Button className="bg-orange-600 hover:bg-orange-500" data-testid="ra-add-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Shortfall
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-slate-800 text-slate-100" data-testid="ra-dialog">
              <DialogHeader><DialogTitle>{editing ? 'Edit Shortfall' : 'New Shortfall'}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Area</Label>
                  <Select value={form.area} onValueChange={(v) => setForm({ ...form, area: v })}>
                    <SelectTrigger data-testid="ra-area" className="bg-slate-950 border-slate-800"><SelectValue /></SelectTrigger>
                    <SelectContent>{AREAS.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Shortfall description</Label>
                  <Textarea data-testid="ra-shortfall" value={form.shortfall} onChange={(e) => setForm({ ...form, shortfall: e.target.value })} rows={2} className="bg-slate-950 border-slate-800" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Impact</Label>
                    <Select value={form.impact} onValueChange={(v) => setForm({ ...form, impact: v })}>
                      <SelectTrigger data-testid="ra-impact" className="bg-slate-950 border-slate-800"><SelectValue /></SelectTrigger>
                      <SelectContent>{IMPACTS.map(i => <SelectItem key={i.v} value={i.v}>{i.l}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Status</Label>
                    <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                      <SelectTrigger data-testid="ra-status" className="bg-slate-950 border-slate-800"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map(s => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Mitigation</Label>
                  <Textarea data-testid="ra-mitigation" value={form.mitigation} onChange={(e) => setForm({ ...form, mitigation: e.target.value })} rows={2} className="bg-slate-950 border-slate-800" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Owner</Label>
                    <Input data-testid="ra-owner" value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} className="bg-slate-950 border-slate-800" />
                  </div>
                  <div>
                    <Label>Target date</Label>
                    <Input data-testid="ra-target" type="date" value={form.target_date} onChange={(e) => setForm({ ...form, target_date: e.target.value })} className="bg-slate-950 border-slate-800" />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={submit} className="bg-orange-600 hover:bg-orange-500" data-testid="ra-save-btn">{editing ? 'Update' : 'Create'}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="bg-slate-900/60 border-slate-800">
            <CardContent className="p-4">
              <div className="text-xs uppercase tracking-wider text-slate-500">Total entries</div>
              <div className="text-2xl font-bold text-white tabular-nums mt-1" data-testid="ra-total">{data.summary.total}</div>
            </CardContent>
          </Card>
          {STATUSES.slice(0, 3).map(s => (
            <Card key={s.v} className="bg-slate-900/60 border-slate-800">
              <CardContent className="p-4">
                <div className="text-xs uppercase tracking-wider text-slate-500">{s.l}</div>
                <div className="text-2xl font-bold text-white tabular-nums mt-1" data-testid={`ra-status-${s.v}`}>{data.summary.by_status[s.v] || 0}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card className="bg-slate-900/60 border-slate-800">
          <CardHeader><CardTitle className="text-white text-lg">Register</CardTitle></CardHeader>
          <CardContent>
            {data.items.length === 0 ? (
              <div className="text-slate-500 text-sm py-8 text-center" data-testid="ra-empty">
                <AlertTriangle className="w-6 h-6 mx-auto text-slate-600 mb-2" />
                No shortfalls logged. Click "Add Shortfall" to register one.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-slate-400 border-b border-slate-800">
                      <th className="text-left p-2">Area</th>
                      <th className="text-left p-2">Shortfall</th>
                      <th className="text-left p-2">Impact</th>
                      <th className="text-left p-2">Mitigation</th>
                      <th className="text-left p-2">Owner</th>
                      <th className="text-left p-2">Target</th>
                      <th className="text-left p-2">Status</th>
                      <th className="text-right p-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map(it => (
                      <tr key={it.id} className="border-b border-slate-800/60 hover:bg-slate-800/40" data-testid={`ra-row-${it.id}`}>
                        <td className="p-2 text-slate-200 whitespace-nowrap">{it.area}</td>
                        <td className="p-2 text-slate-200 max-w-[280px]">{it.shortfall}</td>
                        <td className="p-2"><Badge className={`${impactCls(it.impact)} text-xs`}>{IMPACTS.find(i => i.v === it.impact)?.l}</Badge></td>
                        <td className="p-2 text-slate-400 max-w-[220px] truncate">{it.mitigation || '—'}</td>
                        <td className="p-2 text-slate-300 whitespace-nowrap">{it.owner || '—'}</td>
                        <td className="p-2 text-slate-300 whitespace-nowrap">{it.target_date || '—'}</td>
                        <td className="p-2"><Badge className={`${statusCls(it.status)} text-xs`}>{STATUSES.find(s => s.v === it.status)?.l}</Badge></td>
                        <td className="p-2 text-right whitespace-nowrap">
                          <Button variant="ghost" size="sm" onClick={() => startEdit(it)} className="text-sky-400 hover:text-sky-300" data-testid={`ra-edit-${it.id}`}>
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => remove(it.id)} className="text-rose-400 hover:text-rose-300" data-testid={`ra-delete-${it.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
